import { useEffect, useMemo, useState } from "react";

/**
 * An abstraction over the async functions that will throw an error if an error is thrown during the API call.
 * It does not appear possible to retrigger the Router's data loader once a session exists, so this hook moves the api calls into a useEffect hook,
 * which gives more control over the dependencies and conditions for when the api request is fired.
 * NB: throwing an error inside an async function will not be caught by the error boundary, so we need to catch the error and then set some state to re-throw the error outside of the async function.
 */
export function useApi<T, Args extends unknown[]>(
  callback: (...args: Args) => Promise<T>,
  {
    args,
    skip = false,
    errorCallback,
  }: { args: Args; skip?: boolean; errorCallback?: (error: Error | string) => void },
): { data?: T; error?: string; pending: boolean } {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState(true);

  useEffect(() => {
    if (skip) return;
    let cancelled = false;
    async function init() {
      setPending(true);
      try {
        if (cancelled) {
          // Potential race condition: a cancelled useEffect() may have been replaced by another instances of the same useEffect() pointing to the same state.
          // Therefore, a cancelled useEffect() cannot update the state when it has been cancelled - due to the risk of overwriting data set by the non-cancelled instance.
          return;
        }
        const data = await callback(...args);
        if (cancelled) {
          return;
        }
        setPending(false);
        setData(data);
      } catch (e) {
        if (cancelled) {
          return;
        }
        setPending(false);
        if (errorCallback) {
          errorCallback(e instanceof Error ? e : "An error occurred");
        } else {
          setError(e instanceof Error ? e.message : "An error occurred");
        }
      }
    }
    // Using init as a thenable enables the effect to be cancelled before making the request - prevents unnecessary requests
    Promise.resolve().then(init);
    return () => {
      cancelled = true;
    };
  }, [skip, callback, args, errorCallback]);

  return useMemo(() => ({ data, pending, error }), [data, pending, error]);
}
